import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { errMsg, saveAuth } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";

/**
 * Emergent Google OAuth callback handler.
 * The provider redirects here with `#session_id=<id>` in the URL fragment.
 * We exchange it server-side for our own cookie session and forward to /guest/dashboard.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GuestAuthCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const { lang } = useLang();
  const IT = lang === "it";
  const processed = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-invocation
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      toast.error(IT ? "Nessuna sessione ricevuta da Google. Riprova." : "No session received from Google. Try again.");
      nav("/guest/auth", { replace: true });
      return;
    }

    (async () => {
      try {
        const r = await api.post("/auth/emergent-session", { session_id: sessionId });
        saveAuth(r.data.token, r.data.user);
        // Clear the URL fragment for cleanliness (avoids stale session_id on refresh)
        window.history.replaceState(null, "", "/guest/dashboard");
        toast.success(IT ? `Benvenuta/o ${r.data.user.name || ""}!` : `Welcome ${r.data.user.name || ""}!`);
        nav("/guest/dashboard", { replace: true });
      } catch (e) {
        toast.error(errMsg(e, IT ? "Login Google fallito" : "Google login failed"));
        nav("/guest/auth", { replace: true });
      }
    })();
  }, [location.hash, nav, IT]);

  return (
    <div className="min-h-screen grid place-items-center bg-background" data-testid="guest-auth-callback">
      <div className="text-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto mb-4"/>
        <p className="text-sm text-muted-foreground">{IT ? "Accesso in corso…" : "Signing you in…"}</p>
      </div>
    </div>
  );
}
